





















































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import VueSlider from 'vue-slider-component';
import InfoTip from '@/views/InfoTip.vue';
import { StabilizationConfig } from '@/models/timelapseconfiguration';

@Component({
  components: {
    VueSlider,
    InfoTip,
  },
})
export default class CLAHE extends Vue {
  // StabilizationConfig vient du parent (TimelapseSettings)
  @Prop({ required: true }) readonly stabilization!: StabilizationConfig;
  // Image de preview (dernière photo)
  @Prop({ type: String, default: '' }) readonly previewSrc!: string;
  // Rotation éventuelle de la caméra (pour que le preview reste cohérent)
  @Prop({ type: Number, default: 0 }) readonly rotate!: number;

  // état local de l’accordéon
  private claheOpen = false;
  private clahePreset: 'soft' | 'balanced' | 'strong' | 'custom' = 'balanced';

  // Preview OpenCV
  private cvReady = false;
  private splitRatio = 0.5; // 0..1
  private clahePreviewTimer: any = null;
  private isDragging = false;
  private aspectRatio = 16 / 9;

  private ro: ResizeObserver | null = null;

  // petit alias pratique
  get stab(): any {
    return this.stabilization as any;
  }

  get aspectStyle() {
    return { width: '100%', aspectRatio: String(this.aspectRatio) };
  }

  get claheClipDisplay(): string {
    const v = this.stab.claheClip;
    return v == null ? '0.00' : Number(v).toFixed(2);
  }

  get claheGridDisplay(): number {
    const v = this.stab.claheGrid;
    return v == null ? 0 : Math.round(Number(v));
  }

  get claheBlendDisplay(): string {
    const v = this.stab.claheBlend;
    return v == null ? '0.00' : Number(v).toFixed(2);
  }

  get previewTransform() {
    return this.rotate ? { transform: `rotate(${this.rotate}deg)` } : {};
  }

  public mounted(): void {
    // on s’assure que les clés CLAHE existent
    this.seedClaheKeys(false);

    // ResizeObserver pour adapter le canvas au container
    this.$nextTick(() => {
      const wrap = this.$refs.claheWrap as HTMLElement | undefined;
      if (!wrap) return;

      this.ro = new ResizeObserver(() => {
        this.syncCanvasSize();
        this.scheduleClahePreview(); // plus de if(...)
      });

      this.ro.observe(wrap);
    });
  }

  beforeDestroy(): void {
    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }
    clearTimeout(this.clahePreviewTimer);
    this.endDrag();
  }

  /* ========== Helpers de seed / reset ========== */

  private seedClaheKeys(overwrite: boolean): void {
    const defaults = { claheEnabled: false, claheClip: 1.3, claheGrid: 12, claheBlend: 0.5 };
    (Object.keys(defaults) as Array<keyof typeof defaults>).forEach((k) => {
      if (overwrite || typeof this.stab[k] === 'undefined') {
        // @ts-ignore – on veut Vue.set pour la réactivité
        this.$set(this.stab, k as any, (defaults as any)[k]);
      }
    });

    // si on vient d'initialiser, on considère que c'est "Équilibré"
    if (!overwrite && !this.stab.claheEnabled) {
      this.clahePreset = 'balanced';
    }
  }

  private setClahePreset(p: 'soft' | 'balanced' | 'strong' | 'custom'): void {
    this.clahePreset = p;

    // En mode custom : on n'écrase pas les valeurs, on laisse l'utilisateur jouer avec les sliders
    if (p === 'custom') {
      // on redessine juste avec les valeurs actuelles
      this.scheduleClahePreview();
      return;
    }

    // Valeurs proposées :
    // - soft      : très doux, naturel
    // - balanced  : réglage généraliste
    // - strong    : plus agressif, pour images très plates
    let clip: number;
    let grid: number;
    let blend: number;

    switch (p) {
      case 'soft':
        clip = 1.1;
        grid = 16;
        blend = 0.35;
        break;
      case 'balanced':
        clip = 1.3;
        grid = 12;
        blend = 0.5;
        break;
      case 'strong':
        clip = 1.7;
        grid = 8;
        blend = 0.7;
        break;
    }

    this.$set(this.stab, 'claheClip', clip);
    this.$set(this.stab, 'claheGrid', grid);
    this.$set(this.stab, 'claheBlend', blend);

    // On met à jour l'aperçu
    this.scheduleClahePreview();
  }

  public onToggleClahe(): void {
    if (this.stab.claheEnabled) {
      // On se remet sur le preset "Équilibré" à l'activation
      this.setClahePreset('balanced');
      if (this.claheOpen) this.scheduleClahePreview();
    }
  }

  public onResetClahe(): void {
    // Reset = revenir à "Équilibré"
    this.setClahePreset('balanced');
  }

  public onClaheGridChange(val: number): void {
    this.clahePreset = 'custom';
    const clamped = Math.min(32, Math.max(4, Math.round(Number(val))));
    this.$set(this.stab, 'claheGrid', clamped);
    this.scheduleClahePreview();
  }

  /* ========== Canvas / Resize ========== */

  private syncCanvasSize(): void {
    const wrap = this.$refs.claheWrap as HTMLElement | undefined;
    const cBefore = this.$refs.claheBefore as HTMLCanvasElement | undefined;
    const cAfter = this.$refs.claheAfter as HTMLCanvasElement | undefined;
    if (!wrap || !cBefore || !cAfter) return;

    // largeur affichée
    const displayW = wrap.clientWidth;
    if (!displayW) return;

    // hauteur déduite du ratio
    const displayH = Math.max(1, Math.round(displayW / this.aspectRatio));

    const dpr = window.devicePixelRatio || 1;

    [cBefore, cAfter].forEach((c) => {
      c.style.width = displayW + 'px';
      c.style.height = displayH + 'px';
      c.width = Math.max(1, Math.round(displayW * dpr));
      c.height = Math.max(1, Math.round(displayH * dpr));
    });
  }

  /* ========== Drag du split-handle ========== */

  private startDrag = (e: MouseEvent | TouchEvent) => {
    this.isDragging = true;
    this.updateSplitFromEvent(e);
    window.addEventListener('mousemove', this.onDrag as any, { passive: false });
    window.addEventListener('mouseup', this.endDrag);
    window.addEventListener('touchmove', this.onDrag as any, { passive: false });
    window.addEventListener('touchend', this.endDrag);
  };

  private onDrag = (e: MouseEvent | TouchEvent) => {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updateSplitFromEvent(e);
  };

  private endDrag = () => {
    this.isDragging = false;
    window.removeEventListener('mousemove', this.onDrag as any);
    window.removeEventListener('mouseup', this.endDrag);
    window.removeEventListener('touchmove', this.onDrag as any);
    window.removeEventListener('touchend', this.endDrag);
  };

  private updateSplitFromEvent(e: MouseEvent | TouchEvent): void {
    const wrap = this.$refs.claheWrap as HTMLElement | undefined;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();

    const clientX =
      (e as TouchEvent).touches && (e as TouchEvent).touches.length
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;

    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    this.splitRatio = +(x / rect.width).toFixed(4);
  }

  /* ========== OpenCV / Preview ========== */

  private async ensureCvLoaded(): Promise<void> {
    if (this.cvReady) return;
    const anyWin = window as any;

    await new Promise<void>((resolve) => {
      const check = () => {
        if (anyWin.cv && anyWin.cv.Mat) resolve();
        else setTimeout(check, 80);
      };
      if (anyWin.cv && !anyWin.cv.onRuntimeInitialized) {
        anyWin.cv.onRuntimeInitialized = () => resolve();
      }
      check();
    });

    this.cvReady = true;
  }

  private loadImageForClahe(src: string): Promise<HTMLImageElement> {
    return fetch(src, {
      method: 'GET',
      mode: 'cors', // Force le check CORS (en-tête Origin envoyé)
      cache: 'no-cache', // Ignore le cache navigateur, force fresh fetch
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${response.statusText} (S3: ${
              response.status === 403 ? 'Signature invalide ou CORS manquant' : 'Autre erreur'
            })`
          );
        }
        return response.blob();
      })
      .then((blob) => {
        // Crée une URL locale blob: (non cachable, pas de CORS)
        const blobUrl = URL.createObjectURL(blob);
        return new Promise<HTMLImageElement>((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = 'anonymous'; // Redondant mais safe pour blob:
          im.onload = () => {
            URL.revokeObjectURL(blobUrl); // Libère la mémoire du blob
            resolve(im);
          };
          im.onerror = (err) => {
            URL.revokeObjectURL(blobUrl); // Cleanup même sur erreur
            console.error('[CLAHE] erreur chargement image (blob fallback)', err);
            reject(err);
          };
          im.src = blobUrl; // Charge depuis la URL locale
        });
      })
      .catch((err) => {
        console.error('[CLAHE] fetch échoué (vérifie CORS S3 ou expiration URL)', err);
        // Fallback optionnel : réessaye sans cache-bust (pour debug)
        return new Promise<HTMLImageElement>((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = src + (src.includes('?') ? '&_nocache=' + Date.now() : '?_nocache=' + Date.now());
        });
      });
  }

  private scheduleClahePreview(): void {
    clearTimeout(this.clahePreviewTimer);
    this.clahePreviewTimer = setTimeout(() => this.drawClahePreview(), 120);
  }

  public async drawClahePreview(): Promise<void> {
    if (!this.previewSrc || !this.claheOpen || !this.stab.claheEnabled) return;

    try {
      await this.ensureCvLoaded();
      const cv: any = (window as any).cv;

      const imgEl = await this.loadImageForClahe(this.previewSrc);

      const w = imgEl.naturalWidth || imgEl.width;
      const h = imgEl.naturalHeight || imgEl.height;
      this.aspectRatio = w / Math.max(1, h);

      await this.$nextTick();
      this.syncCanvasSize();

      const cBefore = this.$refs.claheBefore as HTMLCanvasElement;
      const cAfter = this.$refs.claheAfter as HTMLCanvasElement;
      const ctxB = cBefore.getContext('2d')!;

      ctxB.clearRect(0, 0, cBefore.width, cBefore.height);
      ctxB.save();
      if (this.rotate) {
        // simple rotation pour rester cohérent avec le preview principal
        ctxB.translate(cBefore.width / 2, cBefore.height / 2);
        ctxB.rotate((this.rotate * Math.PI) / 180);
        ctxB.drawImage(imgEl, -cBefore.width / 2, -cBefore.height / 2, cBefore.width, cBefore.height);
      } else {
        ctxB.drawImage(imgEl, 0, 0, cBefore.width, cBefore.height);
      }
      ctxB.restore();

      const src = cv.imread(cBefore); // RGBA
      const bgr = new cv.Mat();
      cv.cvtColor(src, bgr, cv.COLOR_RGBA2BGR);

      const ycrcb = new cv.Mat();
      cv.cvtColor(bgr, ycrcb, cv.COLOR_BGR2YCrCb);
      const planes = new cv.MatVector();
      cv.split(ycrcb, planes);
      const Y = planes.get(0);

      const clip = Number(this.stab.claheClip || 1.3);
      const grid = Number(this.stab.claheGrid || 12);
      const blend = Math.max(0, Math.min(1, Number(this.stab.claheBlend || 0.45)));

      const clahe = new cv.CLAHE(clip, new cv.Size(grid, grid));
      const Yeq = new cv.Mat();
      clahe.apply(Y, Yeq);

      const Yf = new cv.Mat();
      const Yeqf = new cv.Mat();
      const diff = new cv.Mat();
      const diffScaled = new cv.Mat();
      const Youtf = new cv.Mat();
      const Yout = new cv.Mat();

      Y.convertTo(Yf, cv.CV_32F);
      Yeq.convertTo(Yeqf, cv.CV_32F);
      cv.subtract(Yeqf, Yf, diff);
      cv.multiply(diff, new cv.Mat(diff.rows, diff.cols, diff.type(), new cv.Scalar(blend)), diffScaled);
      cv.add(Yf, diffScaled, Youtf);
      Youtf.convertTo(Yout, cv.CV_8U);

      planes.set(0, Yout);
      cv.merge(planes, ycrcb);
      const bgrOut = new cv.Mat();
      cv.cvtColor(ycrcb, bgrOut, cv.COLOR_YCrCb2BGR);
      const dst = new cv.Mat();
      cv.cvtColor(bgrOut, dst, cv.COLOR_BGR2RGBA);

      cv.imshow(cAfter, dst);

      // cleanup
      src.delete();
      bgr.delete();
      ycrcb.delete();
      planes.delete();
      Y.delete();
      Yeq.delete();
      Yf.delete();
      Yeqf.delete();
      diff.delete();
      diffScaled.delete();
      Youtf.delete();
      Yout.delete();
      bgrOut.delete();
      dst.delete();
      clahe.delete();
    } catch (err) {
      console.warn('[CLAHE] Preview désactivée (CORS / cv.imread):', err);
    }
  }

  /* ========== Watchers ========== */

  @Watch('claheOpen')
  onClaheOpenChanged(v: boolean) {
    if (v && this.stab.claheEnabled) {
      this.$nextTick(() => {
        this.syncCanvasSize();
        this.scheduleClahePreview();
      });
    }
  }

  @Watch('stabilization', { deep: true })
  onStabChanged() {
    if (this.claheOpen && this.stab.claheEnabled) {
      this.scheduleClahePreview();
    }
  }

  @Watch('previewSrc')
  onPreviewSrcChanged() {
    if (this.claheOpen && this.stab.claheEnabled) {
      this.scheduleClahePreview();
    }
  }
}
